import axios from 'axios';
import { api, getAuthToken } from './authService';
import { cards } from './endpoints';
import { getParams } from '../utils/get-param-util';
import { constructUrl } from '../utils/construct-url';
import { TCreatePhysicalCardPayload, TCreatePhysicalCardResponse, TGetCardRequestQuery, TGetCardsResponse } from '../types/cards/card-types';
import { TErrorResponse } from '../types/error/error-response';
import { message } from 'antd';
import { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL;

// Get transactions pending card registration
export const getPendingCardTransactions = async (cursor = null) => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/admin/transactions`, {
      headers: {
        Authorization: `ClientBearer ${token}`,
      },
      params: {
        disburse_type: 'physical-card',
        status: 'pending-disburse',
        next_cursor: cursor,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

// Get all physical cards
export const getPhysicalCards = async (cursor = null, status = 'issued,dispatched') => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/admin/cards`, {
      headers: {
        Authorization: `ClientBearer ${token}`,
      },
      params: {
        card_type: 'physical',
        status,
        next_cursor: cursor,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

// Get all virtual cards
export const getVirtualCards = async (cursor = null, status = 'issued,dispatched') => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/admin/cards`, {
      headers: {
        Authorization: `ClientBearer ${token}`,
      },
      params: {
        card_type: 'virtual',
        status,
        next_cursor: cursor,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

// Get pending collection cards
export const getPendingCollectionCards = async (cursor = null) => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/admin/cards`, {
      headers: {
        Authorization: `ClientBearer ${token}`,
      },
      params: {
        card_type: 'physical',
        status: 'pending-collection',
        next_cursor: cursor,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

// Register a physical card
export const registerPhysicalCard = async (cardId: string, pan: string) => {
  try {
    const token = getAuthToken();
    const response = await axios.post(
      `${API_BASE_URL}/admin/physical-cards`,
      {
        card_id: cardId,
        pan: pan,
      },
      {
        headers: {
          Authorization: `ClientBearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

// Dispatch Card
export const dispatchACard = async (payload: { card_id?: string; batch_id?: string }) => {
  try {
    const response = await api.post(`${cards.dispatchACard}`, payload);
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    message.error(err.response?.data?.message || err.message);
    throw err.response?.data || err.message;
  }
};

// Get Card
export const getCard = async (id: string) => {
  try {
    const response = await api.get(`${cards.getCard(id)}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error.message;
    }
    throw error;
  }
};

export const getCards = async (param: Partial<TGetCardRequestQuery>): Promise<TGetCardsResponse> => {
  const params = getParams(param);
  const url = constructUrl(`${cards.getCards}`, params);
  try {
    const response = await api.get<TGetCardsResponse>(`${url}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw (error.response?.data as TErrorResponse) || error.message;
    }
    throw error;
  }
};

// Create Physical Card
export const createAPhysicalCard = async (payload: TCreatePhysicalCardPayload): Promise<TCreatePhysicalCardResponse> => {
  try {
    const response = await api.post<TCreatePhysicalCardResponse>(`${cards.createPhysicalCard}`, { ...payload });
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    message.error(err.response?.data?.message || err.message);
    throw err.response?.data || err.message;
  }
};
